import React, { useState,useEffect } from "react";
import { Loader, Segment, Dimmer, Modal, List } from "semantic-ui-react";
import location_icon from "../../../Assets/Icons/location_on.jpg";
import sub_location_icon from "../../../Assets/Icons/pin_drop.png";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { Country } from "country-state-city";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import PeopleForm from "./PeopleForm";
import { PhoneNumberUtil } from "google-libphonenumber";
import { selectTranslations } from "../../config/i18n/slice";
import {
  AdminGreyIcon,
  CloseIcon,
  LimitedAdminIcon,
  RequesterIcon,
  TechnicianIcon,
  ViewerIcon,
} from "../../../Assets/Icons/svg";
import ToolTip from "react-power-tooltip";
import { fetchPeopleInitData } from "../../../redux/reducers/peopleReducer";
import { Resizable } from "re-resizable";
import useNewCancelModal from "../../../hooks/Messages/useNewCncelModal";
import { getAllJobTitles } from "../../config/sharedApi/jobTitleApis";
import {
  createNewUser,
  createNewUserProfile,
  updateUserRole,
  verifyPersonEmailAndPhone,
} from "../../config/api/peopleApis";
import { validateEmail } from "../../config/common/commonFunctions";
import { getAllParentLocations } from "../../config/sharedApi/locationApis";
import { uploadFile } from "../../config/api/configApis";
import vr from "../../../utilFunctions/validationRules";

const {
  lenBetween,
  isPositiveNumber,
  isNumberInRange,
  isDateString,
} = vr;

const phoneUtil = PhoneNumberUtil.getInstance();

const addCustomYupMethods = (t) => {
  Yup.addMethod(Yup.string, 'phone', function(message = t?.people?.invalid_phone || "Invalid phone number") {
    return this.test('phone', message, function(value) {
      const { path, createError } = this;
      if (!value) return true;
      return isPhoneValid(value) || createError({ path, message });
    });
  });

  Yup.addMethod(Yup.string, 'lenBetween', function(min, max, message) {
    return this.test('lenBetween', message, function(value) {
      const { path, createError } = this;
      if (!value) return true;
      return lenBetween(value, min, max) || createError({ path, message });
    });
  });

  Yup.addMethod(Yup.number, 'positiveOrZero', function(message = t?.common?.must_be_positive || "Must be 0 or higher") {
    return this.test('positiveOrZero', message, function(value) {
      const { path, createError } = this;
      if (value == null) return true;
      return isPositiveNumber(Number(value)) || createError({ path, message });
    });
  });

  Yup.addMethod(Yup.number, 'inRange', function(min, max, message) {
    return this.test('inRange', message, function(value) {
      const { path, createError } = this;
      if (value == null) return true;
      return isNumberInRange(Number(value), min, max) || createError({ path, message });
    });
  });

  Yup.addMethod(Yup.string, 'validDate', function(message = t?.common?.invalid_date || "Invalid date") {
    return this.test('validDate', message, function(value) {
      const { path, createError } = this;
      if (!value) return true;
      return isDateString(value) || createError({ path, message });
    });
  });
};

const isPhoneValid = (phone) => {
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch (error) {
    return false;
  }
};

const firstTabValidationFields = [
  "firstName",
  "lastName",
  "department",
  "supervisor",
  "role",
  "email",
  "phone",
  "whpw",
  "whpd",
  "jobTitle",
  "workPermitNo",
  "hourRate",
  "shifts",
  "days",
  "locations",
];
const secondTabValidationFields = ["emergencyPhone","address","identificationNo","childrenNo","passportNo","visaNo"];

function Add({
  toggled,
  untoggle,
  setFilterOptions,
  setSearchTitle,
  setSortData,
  roleData,
  allIds,
}) {
  const t = useSelector(selectTranslations);

  const MSG = {
    required: t.common.required_field,
    whpdRange: t.people?.errors?.whpd_between || "Hours/day must be 0–24",
    whpwRange: t.people?.errors?.whpw_between || "Hours/week must be 0–168",
    positiveOrZero: t.common.must_be_positive || "Must be 0 or higher",
    invalidOption: t.common.invalid_option || "Invalid option",
    atLeastOneDay: t.people?.errors?.select_one_day || "Select at least one working day",
    atLeastOneShift: t.people?.errors?.select_one_shift || "Select at least one shift",
    oneLocation: t.people?.errors?.select_location || "Select at least one location",
    invalidPhone: t.people?.invalid_phone || "Invalid phone number",
    invalidDate: t.common.invalid_date || "Invalid date",
    minmax: (min, max) =>
        (t.common.minmax_chars || "Must be {min}–{max} characters")
            .replace("{min}", String(min))
            .replace("{max}", String(max)),
  };

  const organisationId = useSelector((state) => {
    if (state?.auth?.user?.organisation) {
      return state.auth.user.organisation.id;
    } else {
      return null;
    }
  });
  const dispatch = useDispatch();
  const { initData } = useSelector((state) => ({
    initData: state.people.peopleInitData,
  }));
  const selectedLanguage = useSelector((state) => {
    if (state?.i18n?.lang) {
      return state.i18n.lang;
    } else {
      return "en";
    }
  });

  const trim = (v) => (typeof v === 'string' ? v.trim() : v);
  const toNum = (orig) => {
    if (orig === '' || orig === null || orig === undefined) return undefined;
    const n = Number(orig);
    return Number.isNaN(n) ? NaN : n;
  };
  const toDigits = (v) => String(v == null ? '' : v).replace(/\D+/g, '');

  const MIN = (n) => `Min ${n} characters`;
  const MAX = (n) => `Max ${n} characters`;
  const REQ = 'Required';
  const INVALID_PHONE = 'Phone must be 9–15 digits';
  const RANGE_0_168 = 'Must be between 0 and 168';
  const POSITIVE_OR_ZERO = 'Must be 0 or higher';
  const INVALID_OPT = 'Invalid option';
  const INVALID_DATE = 'Invalid date';

  const textLen = (min, max) => {
    let y = Yup.string().transform(trim).min(min, MIN(min));
    if (max) y = y.max(max, MAX(max));
    return y;
  };


  const { setNewCancelModal, NewCancelModal } = useNewCancelModal();
  const [showCancleTooltip, setCancleTooltip] = useState(false);
  const [modalWidth, setModalWidth] = useState(
    Math.max(window.innerWidth / 2, 514)
  );

  const {
    PersonValidationSchema,
    PersonTechnicianValidationSchema,
    PersonRequesterValidationSchema,
    PersonLimitedValidationSchema,
  } = React.useMemo(() => {
    addCustomYupMethods(t);

    const mm = (min, max) =>
        t.common.minmax_chars
            ?.replace?.("{min}", String(min))
            ?.replace?.("{max}", String(max));

    const Base = Yup.object().shape({
      firstName: Yup.string().required(MSG.required).lenBetween(3, 25, MSG.minmax(3, 25)),
      lastName:  Yup.string().required(MSG.required).lenBetween(3, 25, MSG.minmax(3, 25)),
      role: Yup.string().required(MSG.required),
      email: Yup.string().email(t.people.invalid_email).required(MSG.required).lenBetween(6, 254, MSG.minmax(6, 254)),
      phone: Yup.string().required(MSG.required).phone(MSG.invalidPhone),
      whpd: Yup.number().inRange(0, 24, MSG.whpdRange).required(MSG.required),
      whpw: Yup.number().inRange(0, 168, MSG.whpwRange).required(MSG.required),
      hourRate: Yup.number().positiveOrZero(MSG.positiveOrZero).required(MSG.required),
      address: Yup.string().required(MSG.required).lenBetween(3, 255, MSG.minmax(3, 255)),
      martialStatus: Yup.string().oneOf(['single','married'], MSG.invalidOption).required(MSG.required),
      educationLevel: Yup.string().oneOf(['highSchoolDegree','diplomaDegree','bachelorsDegree','mastersDegree','doctorateDegree','other'], MSG.invalidOption).required(MSG.required),
      identificationNo: Yup.string().required(MSG.required).lenBetween(3, 50, MSG.minmax(3, 50)),
      gender: Yup.string().oneOf(['male','female'], MSG.invalidOption).required(MSG.required),
      childrenNo: Yup.number().positiveOrZero(MSG.positiveOrZero).required(MSG.required),
      emergencyName: Yup.string().nullable(),
      emergencyPhone: Yup.string().nullable().phone(MSG.invalidPhone),
      fieldOfStudy: Yup.string().nullable().lenBetween(1, 100, MSG.minmax(1, 100)),
      nationality: Yup.string().nullable().lenBetween(1, 100, MSG.minmax(1, 100)),
      passportNo: Yup.string().nullable().lenBetween(3, 50, MSG.minmax(3, 50)),
      dob: Yup.string().nullable().validDate(MSG.invalidDate),
      cob: Yup.string().nullable(),
      visaNo: Yup.string().nullable().lenBetween(3, 50, MSG.minmax(3, 50)),
      workPermitNo: Yup.string().nullable().lenBetween(3, 50, MSG.minmax(3, 50)),
      visaExpireDate: Yup.string().nullable().validDate(MSG.invalidDate),
      registrationNo: Yup.number().nullable().positiveOrZero(MSG.positiveOrZero),
      badgeId: Yup.string().nullable(),
      department: Yup.string().nullable(),
      contractStart: Yup.string().nullable().validDate(MSG.invalidDate),
      contractEnd: Yup.string().nullable().validDate(MSG.invalidDate),

      shifts: Yup.object().shape({
        shiftA: Yup.boolean().required(),
        shiftB: Yup.boolean().required(),
        shiftC: Yup.boolean().required(),
      }).test("at-least-one-true", MSG.atLeastOneShift, v => v && (v.shiftA || v.shiftB || v.shiftC)),

      days: Yup.object().shape({
        sat: Yup.boolean().required(),
        sun: Yup.boolean().required(),
        mon: Yup.boolean().required(),
        tue: Yup.boolean().required(),
        wed: Yup.boolean().required(),
        thu: Yup.boolean().required(),
        fri: Yup.boolean().required(),
      }).test("at-least-one-true", MSG.atLeastOneDay,
          v => v && (v.sat||v.sun||v.mon||v.tue||v.wed||v.thu||v.fri)
      ),

      locations: Yup.array().of(Yup.number()).min(1, MSG.oneLocation).required(MSG.oneLocation),
    });

    return {
      PersonValidationSchema: Base,
      PersonLimitedValidationSchema: Base.shape({
        supervisor: Yup.string().required(t.common.required_field),
      }),
      PersonTechnicianValidationSchema: Base.shape({
        supervisor: Yup.string().required(t.common.required_field),
      }),
      PersonRequesterValidationSchema: Yup.object().shape({
        role: Yup.string().required(t.common.required_field),
        locations: Yup.array().of(Yup.number()).min(1, t.common.required_field).required(t.common.required_field),
        firstName: Yup.string().required(t.common.required_field).lenBetween(3, 100, mm(3, 100)),
        lastName:  Yup.string().required(t.common.required_field).lenBetween(3, 100, mm(3, 100)),
        phone: Yup.string().required(MSG.required).phone(MSG.invalidPhone),
        address: Yup.string().required(t.common.required_field).lenBetween(3, 255, mm(3, 255)),
        martialStatus: Yup.string().oneOf(['single','married'], t.common.invalid_option).required(t.common.required_field),
        identificationNo: Yup.string().required(t.common.required_field).lenBetween(3, 50, mm(3, 50)),
        gender: Yup.string().oneOf(['male','female'], t.common.invalid_option).required(t.common.required_field),
        whpd: Yup.number().nullable().positiveOrZero(MSG.positiveOrZero),
        whpw: Yup.number().nullable().inRange(0, 168, MSG.whpwRange),
        hourRate: Yup.number().nullable().positiveOrZero(MSG.positiveOrZero),
        educationLevel: Yup.string().nullable().oneOf(['highSchoolDegree','diplomaDegree','bachelorsDegree','mastersDegree','doctorateDegree','other']),
        childrenNo: Yup.number().nullable().positiveOrZero(),
        emergencyName: Yup.string().nullable(),
        emergencyPhone: Yup.string().nullable().phone(),
        fieldOfStudy: Yup.string().nullable(),
        nationality: Yup.string().nullable(),
        passportNo: Yup.string().nullable(),
        dob: Yup.string().nullable().validDate(MSG.invalidDate),
        cob: Yup.string().nullable(),
        visaNo: Yup.string().nullable(),
        workPermitNo: Yup.string().nullable(),
        visaExpireDate: Yup.string().nullable().validDate(MSG.invalidDate),
        registrationNo: Yup.number().nullable().positiveOrZero(),
        badgeId: Yup.string().nullable(),
        department: Yup.string().nullable(),
        contractStart: Yup.string().nullable().validDate(MSG.invalidDate),
        contractEnd: Yup.string().nullable().validDate(MSG.invalidDate),
        shifts: Yup.object({ shiftA: Yup.boolean().required(), shiftB: Yup.boolean().required(), shiftC: Yup.boolean().required() }).nullable(),
        days: Yup.object({ sat:Yup.boolean().required(), sun:Yup.boolean().required(), mon:Yup.boolean().required(), tue:Yup.boolean().required(), wed:Yup.boolean().required(), thu:Yup.boolean().required(), fri:Yup.boolean().required() }).nullable(),
      }),
    };
  }, [t]);

  const currentUserId = useSelector((state) => {
    if (state?.auth?.user?.id) {
      return state.auth.user.id;
    } else {
      return null;
    }
  });
  const [initChange, setInitChange] = useState(true);
  const [activePane, setActivePane] = useState(0);
  const [personalImgId, setPersonalImgId] = useState();
  const [file, setFile] = useState(""); //
  const [imageSrc, setImageSrc] = useState("");
  const [buttonStatus, setButtonStatus] = useState(0);
  const [isLoadingActive, activateLoading] = useState(false);
  const [isAsideModalOpen, toggleAsideModalOpen] = useState(false);
  const [successModal, toggleSuccessModal] = useState(false);
  const [responseData, setResponseData] = useState({});

  const [dropdownOptions, setDropdownOptions] = useState({
    locations: {
      initOptions: [],
      options: [],
    },
    roles: [
      {
        key: 0,
        value: 5,
        text: t.people.add_modal.admin,
        icon: <AdminGreyIcon />,
        description: t.people.add_modal.admin_description,
      },

      {
        key: 2,
        value: 9,
        text: t.people.add_modal.limited_admin,
        icon: <LimitedAdminIcon />,
        description: t.people.add_modal.limited_admin_description,
      },
      {
        key: 1,
        value: 8,
        text: t.people.add_modal.technician,
        icon: <TechnicianIcon />,
        description: t.people.add_modal.user_description,
      },

      {
        key: 3,
        value: 3,
        text: t.people.add_modal.viewer,
        icon: <ViewerIcon />,
        description: t.people.add_modal.viewer_description,
      },
      {
        key: 2,
        value: 6,
        text: t.people.add_modal.requester,
        icon: <RequesterIcon />,
        description: t.people.add_modal.requester_desc,
      },
    ],

    martialStatus: [
      {
        key: 0,
        value: "single",
        text: t.people.personal_info_form.martial_option.single,
      },

      {
        key: 1,
        value: "married",
        text: t.people.personal_info_form.martial_option.married,
      },
    ],
    userRole: [
      {
        key: 0,
        value: 5,
        text: t.people.add_modal.admin,
      },

      {
        key: 9,
        value: 9,
        text: t.people.add_modal.limited_admin,
      },
      {
        key: 8,
        value: 8,
        text: t.people.add_modal.technician,
      },
      {
        key: 3,
        value: 3,
        text: t.people.add_modal.viewer,
      },
      {
        key: 6,
        value: 6,
        text: t.people.add_modal.requester,
      },
    ],
    limitedRoles: [
      {
        key: 9,
        value: 9,
        text: t.people.add_modal.limited_admin,
      },
      {
        key: 8,
        value: 8,
        text: t.people.add_modal.technician,
      },
      {
        key: 3,
        value: 3,
        text: t.people.add_modal.viewer,
      },
      {
        key: 6,
        value: 6,
        text: t.people.add_modal.requester,
      },
    ],
    childrenNumber: [
      {
        key: 0,
        value: 0,
        text: "0",
      },

      {
        key: 1,
        value: 1,
        text: "1",
      },
      {
        key: 2,
        value: 2,
        text: "2",
      },
      {
        key: 3,
        value: 3,
        text: "3",
      },
      {
        key: 4,
        value: 4,
        text: "4",
      },
      {
        key: 5,
        value: 5,
        text: "5",
      },
      {
        key: 6,
        value: 6,
        text: "6",
      },
      {
        key: 7,
        value: 7,
        text: "7",
      },
    ],

    studyFiled: [
      {
        key: 0,
        value: "Agriculture and Forestry",
        text: t.people.studyField.agriculture,
      },

      {
        key: 1,
        value: "Architecture and Urban Planning",
        text: t.people.studyField.agricultureUrban,
      },
      {
        key: 2,
        value: "Arts and Humanities",
        text: t.people.studyField.arts,
      },
      {
        key: 3,
        value: "Astronomy and Astrophysics",
        text: t.people.studyField.astronomy,
      },
      {
        key: 4,
        value: "Business and Economics",
        text: t.people.studyField.economics,
      },
      {
        key: 5,
        value: "Communication and Media Studies",
        text: t.people.studyField.mediaStudy,
      },
      {
        key: 6,
        value: "Computer Science and Information Technology",
        text: t.people.studyField.computerScience,
      },
      {
        key: 7,
        value: "Cultural Studies",
        text: t.people.studyField.cultural,
      },
      {
        key: 8,
        value: "Education",
        text: t.people.studyField.education,
      },
      {
        key: 9,
        value: "Engineering and Technology",
        text: t.people.studyField.engineering,
      },
      {
        key: 10,
        value: "Environmental Science",
        text: t.people.studyField.environmental,
      },
      {
        key: 11,
        value: "Fine Arts and Design",
        text: t.people.studyField.fine_arts,
      },
      {
        key: 12,
        value: "Geology and Earth Sciences",
        text: t.people.studyField.geology,
      },
      {
        key: 13,
        value: "Health and Medicine",
        text: t.people.studyField.health,
      },
      {
        key: 14,
        value: "Hospitality and Tourism",
        text: t.people.studyField.hospitality,
      },
      {
        key: 15,
        value: "Law and Legal Studies",
        text: t.people.studyField.law,
      },
      {
        key: 16,
        value: "Library and Information Science",
        text: t.people.studyField.library,
      },
      {
        key: 17,
        value: "Linguistics",
        text: t.people.studyField.linguistics,
      },
      {
        key: 18,
        value: "Mathematics and Statistics",
        text: t.people.studyField.mathematics,
      },
      {
        key: 19,
        value: "Natural Sciences",
        text: t.people.studyField.naturalSciences,
      },
      {
        key: 20,
        value: "Performing Arts",
        text: t.people.studyField.performingArts,
      },
      {
        key: 21,
        value: "Philosophy",
        text: t.people.studyField.philosophy,
      },
      {
        key: 22,
        value: "Physical Education and Sports Science",
        text: t.people.studyField.physicalEducation,
      },
      {
        key: 23,
        value: "Political Science",
        text: t.people.studyField.politicalScience,
      },
      {
        key: 24,
        value: "Psychology",
        text: t.people.studyField.psychology,
      },
      {
        key: 25,
        value: "Public Administration",
        text: t.people.studyField.publicAdministration,
      },
      {
        key: 26,
        value: "Religious Studies",
        text: t.people.studyField.religiousStudies,
      },
      {
        key: 27,
        value: "Social Sciences",
        text: t.people.studyField.socialSciences,
      },
      {
        key: 28,
        value: "Sociology",
        text: t.people.studyField.sociology,
      },
    ],

    educationLevel: [
      {
        key: 0,
        value: "highSchoolDegree",
        text: t.people.personal_info_form.education_options.high_school,
      },
      {
        key: 1,
        value: "diplomaDegree",
        text: t.people.personal_info_form.education_options.diploma,
      },
      {
        key: 2,
        value: "bachelorsDegree",
        text: t.people.personal_info_form.education_options.bachelors,
      },
      {
        key: 3,
        value: "mastersDegree",
        text: t.people.personal_info_form.education_options.masters,
      },
      {
        key: 4,
        value: "doctorateDegree",
        text: t.people.personal_info_form.education_options.doctorate,
      },
      {
        key: 5,
        value: "other",
        text: t.people.personal_info_form.education_options.other,
      },
    ],
    nationalities: [],

    gender: [
      {
        key: 0,
        value: "male",
        text: t.people.personal_info_form.gender_options.gender_male,
      },

      {
        key: 1,
        value: "female",
        text: t.people.personal_info_form.gender_options.gender_female,
      },
    ],

    users: [],
    teams: [],
    jobTitle: [],
  });
  const [data, setData] = useState({
    locations: [],
    selectedLocation: [],
    teams: [],
    hourRate: "",
    whpd: "",
    whpw: "",
    department: "",
    contractStart: "",
    contractEnd: "",
    shifts: {
      shiftA: true,
      shiftB: false,
      shiftC: false,
    },
    days: {
      sat: false,
      sun: false,
      mon: false,
      tue: false,
      wed: false,
      thu: false,
      fri: false,
    },
    firstName: "",
    lastName: "",
    username: "",
    address: "",
    email: "",
    countryCode: "962",
    phone: "",
    martialStatus: "single",
    emergencyName: "",
    emergencyCountryCode: "962",
    emergencyPhone: "",
    educationLevel: "highSchoolDegree",
    fieldOfStudy: "",
    nationality: "",
    identificationNo: "",
    passportNo: "",
    gender: "male",
    dob: null,
    cob: "",
    childrenNo: "",
    visaNo: "",
    workPermitNo: "",
    visaExpireDate: null,
    supervisor: "",
    jobTitle: "",
    role: "",
    registrationNo: "",
    badgeId: "",
  });

  const [attachments, setAttachments] = useState({
    contractCopy: null,
    personalPicture: null,
    passportCopy: null,
    idCopyFront: null,
    visaCopy: null,
    covidCopy: [],
  });

  const [validation, setValidation] = useState({
    firstName: {
      error: false,
      pattern: (firstName) => firstName != "",
      skipped: false,
      message: t.common.required_field,
    },
    lastName: {
      error: false,
      pattern: (lastName) => lastName != "",
      skipped: false,
      message: t.common.required_field,
    },

    email: {
      error: false,
      pattern: (email) => validateEmail(email),
      skipped: false,
      message: t.people.invalid_email,
    },
    phone: {
      error: false,
      pattern: (phone) => phone != "",
      skipped: false,
      message: t.common.required_field,
    },
    jobTitle: {
      error: false,
      pattern: (title) => title != "",
      skipped: false,
      message: t.common.required_field,
    },
    hourRate: {
      error: false,
      pattern: (hourRate) => hourRate != "",
      skipped: false,
      message: t.common.required_field,
    },
    shifts: {
      error: false,
      pattern: (shifts) => shifts.some((el) => el != false),
      skipped: false,
      message: t.common.required_field,
    },
    days: {
      error: false,
      pattern: (days) => days.some((el) => el != false),
      skipped: false,
      message: t.common.required_field,
    },
    locations: {
      error: false,
      pattern: (locations) =>
        locations && locations.length > 0 && locations != "",
      skipped: false,
      message: t.common.required_field,
    },
  });

  const resetForm = () => {
    setData({
      locations: [],
      selectedLocation: [],
      teams: [],
      hourRate: "",
      whpd: "",
      whpw: "",
      department: "",
      contractStart: "",
      contractEnd: "",
      shifts: {
        shiftA: true,
        shiftB: false,
        shiftC: false,
      },
      days: {
        sat: false,
        sun: false,
        mon: false,
        tue: false,
        wed: false,
        thu: false,
        fri: false,
      },
      firstName: "",
      lastName: "",

      address: "",
      email: "",
      countryCode: "962",
      phone: "",
      martialStatus: "single",
      emergencyName: "",
      emergencyCountryCode: "962",
      emergencyPhone: "",
      educationLevel: "highSchoolDegree",
      fieldOfStudy: "",
      nationality: "",
      identificationNo: "",
      passportNo: "",
      gender: "male",
      dob: null,
      cob: "",
      childrenNo: "",
      visaNo: "",
      workPermitNo: "",
      visaExpireDate: null,
      supervisor: "",
      jobTitle: "",
      role: "",
      registrationNo: "",
      badgeId: "",
    });

    setModalWidth(Math.max(window.innerWidth / 2, 514));

    setAttachments({
      contractCopy: null,
      personalPicture: null,
      passportCopy: null,
      idCopyFront: null,
      visaCopy: null,
      covidCopy: [],
    });

    setFile("");
    setImageSrc("");
    setPersonalImgId(null);

    setActivePane(0);
  };

  const fetchLocations = async () => {
    const fetchData = await getAllParentLocations(
      organisationId,
      "",
      1,
      99999,
      [],
      [],
      [],
      ["name:asc"]
    );
    let the_options = [];
    let parentOptions = [];
    let subOptions = [];
    if (fetchData?.response?.length > 0) {
      fetchData.response.forEach((el) => {
        if (!(roleData === "super_admin")) {
          if (allIds?.includes(el.id)) {
            parentOptions.push({
              key: el.id,
              value: el.id,
              text: el.attributes.name,
              image: { src: location_icon },
              className: "main-location-dropdown-option",
              disabled: false,
            });

            if (el?.attributes?.subLocations?.data?.length > 0) {
              el.attributes.subLocations.data.forEach((subEl) => {
                if (allIds?.includes(subEl.id)) {
                  subOptions.push({
                    key: subEl.id,
                    value: subEl.id,
                    text: subEl.attributes.name,
                    image: { src: sub_location_icon },
                    className: "sub-location-dropdown-option",
                    parentId: el.id,
                  });
                }
              });
            }
          } else {
            const hasMatchingSubLocation = el?.attributes?.subLocations?.data?.some(
              (subEl) => allIds?.includes(subEl.id)
            );

            if (hasMatchingSubLocation) {
              parentOptions.push({
                key: el.id,
                value: el.id,
                text: el.attributes.name,
                image: { src: location_icon },
                className: "main-location-dropdown-option",
                disabled: true,
              });

              el?.attributes?.subLocations?.data?.length > 0 &&
                el.attributes.subLocations.data.forEach((subEl) => {
                  if (allIds?.includes(subEl.id)) {
                    subOptions.push({
                      key: subEl.id,
                      value: subEl.id,
                      text: subEl.attributes.name,
                      image: { src: sub_location_icon },
                      className: "sub-location-dropdown-option",
                      parentId: el.id,
                    });
                  }
                });
            }
          }
        } else {
          parentOptions.push({
            key: el.id,
            value: el.id,
            disabled: false,
            text: el.attributes.name,
            image: { src: location_icon },
            className: "main-location-dropdown-option",
          });

          if (el?.attributes?.subLocations?.data?.length > 0) {
            el.attributes.subLocations.data.forEach((subEl) => {
              subOptions.push({
                key: subEl.id,
                value: subEl.id,
                text: subEl.attributes.name,
                image: { src: sub_location_icon },
                className: "sub-location-dropdown-option",
                parentId: el.id,
              });
            });
          }
        }
      });

      subOptions.sort((a, b) => a.text.localeCompare(b.text));

      parentOptions.forEach((parentOption) => {
        the_options.push(parentOption);

        subOptions.forEach((subOption) => {
          if (subOption.parentId === parentOption.key) {
            the_options.push(subOption);
          }
        });
      });

      setDropdownOptions((prev) => ({
        ...prev,
        locations: {
          options: the_options,
          initOptions: the_options,
        },
      }));

      return the_options;
    }
  };

  const fetchCountries = () => {
    const fetchData = Country.getAllCountries();
    let the_options = [];
    if (fetchData) {
      the_options = fetchData.map((el) => {
        return {
          key: el?.isoCode?.toLowerCase(),
          value: el?.isoCode,
          text: el.name,
          flag: el?.isoCode?.toLowerCase(),
        };
      });

      setDropdownOptions({ ...dropdownOptions, nationalities: the_options });
    }
  };

  const fetchJobTitleData = async () => {
    const fetchData = await getAllJobTitles(organisationId);

    if (fetchData.response) {
      let the_options = fetchData.response.map((el) => {
        return {
          key: el.id,
          value: el.id,
          text: el.attributes.title,
        };
      });

      setDropdownOptions({
        ...dropdownOptions,
        jobTitle: the_options,
      });

      return the_options;
    }
  };

  const onFileChange = (files, name) => {
    setInitChange(true);
    var randomnumber = Math.floor(Math.random() * (0 - 100 + 1)) + 100;

    if (name == "personalPicture") {
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setImageSrc(reader.result);
      });
      reader.readAsDataURL(files[0]);

      setFile({
        name: files[0]?.name,
        type: files[0]?.type,
      });
    } else {
      setAttachments({
        ...attachments,
        [name]: {
          file: files[0],
          id: randomnumber,
          name: files[0].name,
          type: files[0].type,
          cancelPopup: false,
          isChanged: true,
        },
      });
    }
  };

  const onCertificateFileChange = (files, name) => {
    setInitChange(true);
    let newFiles = [];
    Array.from(files).forEach((file) => {
      var randomnumber = Math.floor(Math.random() * (0 - 100 + 1)) + 100;
      newFiles.push({
        file: file,
        id: randomnumber,
        name: file.name,
        type: file.type,
        cancelPopup: false,
        isChanged: true,
      });
    });
    setAttachments({
      ...attachments,
      [name]: [...attachments[name], ...newFiles],
    });
  };

  const uploadFilessHandler = async (files = []) => {
    let theFiles = files.filter((el) => el.isChanged);
    theFiles = theFiles.map((el) => el.file);
    if (theFiles.length > 0) {
      let reponse = await uploadFile(theFiles);
      return reponse;
    } else {
      return false;
    }
  };

  const createNewUserHandler = async (data) => {
    try {
      setButtonStatus(1);

      const contractCopyResponse =
        attachments.contractCopy && attachments.contractCopy.isChanged
          ? await uploadFile([attachments.contractCopy.file])
          : null;
      const passportCopyResponse =
        attachments.passportCopy && attachments.passportCopy.isChanged
          ? await uploadFile([attachments.passportCopy.file])
          : null;
      const idFrontCopyResponse =
        attachments.idCopyFront && attachments.idCopyFront.isChanged
          ? await uploadFile([attachments.idCopyFront.file])
          : null;

      const visaCopyResponse =
        attachments.visaCopy && attachments.visaCopy.isChanged
          ? await uploadFile([attachments.visaCopy.file])
          : null;
      const covidCopyResponse =
        attachments.covidCopy && attachments.covidCopy.length
          ? await uploadFilessHandler(attachments.covidCopy)
          : null;

      const response = await createNewUser(
        data.firstName.trim() + " " + data.lastName.trim(),
        data.role,
        data.phone,
        data.email ? data.email : null,
        data.teams ? data.teams : null,
        data.firstName.trim(),
        data.lastName.trim(),
        data.role == 8 || data.role == 9 || data.role == 5? data?.supervisor || null : null,
        data.selectedLocation ? data.selectedLocation : null,
        organisationId,
        currentUserId,
        personalImgId ? personalImgId : null
      );

      if (response.status == 200) {
        setResponseData({
          id: response.data.user.id,
          name: response.data.user.firstName,
        });

        const roleUpdated = await updateUserRole(
          data.role,
          response.data.user.id
        );

        if (roleUpdated.status == 200) {
          let resData = await createUserProfileHandler(
            response.data.user.id,

            contractCopyResponse?.status == 200
              ? contractCopyResponse.data[0].id
              : null,
            personalImgId ? personalImgId : null,

            passportCopyResponse?.status == 200
              ? passportCopyResponse.data[0].id
              : null,
            idFrontCopyResponse?.status == 200
              ? idFrontCopyResponse.data[0].id
              : null,

            visaCopyResponse?.status == 200
              ? visaCopyResponse.data[0].id
              : null,
            covidCopyResponse?.status == 200
              ? covidCopyResponse.data.map((el) => el.id)
              : [],
            data
          );
          if (resData == 200) {
            toast.success(t.people.title + " " + t.common.added_success);
            if (initData?.length == 0) {
              const fetchData = await dispatch(
                fetchPeopleInitData({
                  role: [
                    "user",
                    "supervisor",
                    "view_only",
                    "admin",
                    "requester",
                    "limited_admin",
                    "super_admin",
                  ],
                  organisationId,
                  roleData,
                  allIds,
                })
              );
            }

            setSortData([]);
            setFilterOptions({
              role: [],
              supervisors: [],
              jobTitle: [],
              teams: [],
              status: [],
              rate: {
                from: null,
                to: null,
              },
            });
            setSearchTitle("");
          }
          // }
        }
      }
    } catch (err) {
      if (
        err.message == "This email is already exists" ||
        err.message == "This phone number and email already exist" ||
        err.message == "This phone is already exists"
      ) {
        toast.error(
          t.people.form_validation_errors[err.message] || err.message
        );
      } else {
        toast.error(err.message);
      }
      setButtonStatus(0);
    }
  };

  const changeTab = async (tab, errors, setFieldTouched, values) => {
    let errorFields = Object.keys(errors);
    let hasError = false;
    if (activePane > tab) {
      setActivePane(tab);
      return;
    }
    if (tab == 1) {
      if (errorFields && errorFields.length) {
        for (let i = 0; i < firstTabValidationFields.length; i++) {
          if (errorFields.includes(firstTabValidationFields[i])) {
            hasError = true;
            setFieldTouched(firstTabValidationFields[i], true);
          }
        }
      }
      if (!hasError) {
        let validateEmailAndPhone = await verifyPersonEmailAndPhone(
          values.userId || null,
          values.email ? values.email : null,
          values.phone,
          organisationId,
          values.role
        );
        if (validateEmailAndPhone && validateEmailAndPhone.status != 200) {
          hasError = true;
          toast.error(
            t.people.form_validation_errors[validateEmailAndPhone.error] ||
              validateEmailAndPhone.error
          );
        }
      }
    } else if (tab == 2) {
      if (errorFields && errorFields.length) {
        for (let i = 0; i < firstTabValidationFields.length; i++) {
          if (errorFields.includes(firstTabValidationFields[i])) {
            hasError = true;
            setFieldTouched(firstTabValidationFields[i], true);
          }
        }
      }

      if (!hasError) {
        let validateEmailAndPhone = await verifyPersonEmailAndPhone(
          values.userId || null,
          values.email ? values.email : null,
          values.phone,
          organisationId,
          values.role
        );
        if (validateEmailAndPhone && validateEmailAndPhone.status != 200) {
          hasError = true;
          toast.error(
            t.people.form_validation_errors[validateEmailAndPhone.error] ||
              validateEmailAndPhone.error
          );
        }
      }
      if (errorFields && errorFields.length) {
        for (let i = 0; i < firstTabValidationFields.length; i++) {
          if (errorFields.includes(firstTabValidationFields[i])) {
            hasError = true;
            setFieldTouched(firstTabValidationFields[i], true);
          }
        }

        for (let i = 0; i < secondTabValidationFields.length; i++) {
          if (errorFields.includes(secondTabValidationFields[i])) {
            hasError = true;
            setFieldTouched(secondTabValidationFields[i], true);
          }
        }
      }
    }
    if (!hasError) {
      setActivePane(tab);
    }
  };

  const createUserProfileHandler = async (
    id,
    contractCopy,
    personalPicture,
    passportCopy,
    idCopyFront,
    visaCopy,
    covidCopy,
    data
  ) => {
    const response = await createNewUserProfile(
      id,
      data.phone,
      String(data.address),
      String(data.emergencyName),
      data.emergencyPhone || null,
      String(data.educationLevel),
      String(data.fieldOfStudy),
      String(data.martialStatus),
      String(data.nationality),
      String(data.passportNo),
      String(data.identificationNo),
      data.dob ? format(new Date(data.dob), "yyyy-MM-dd") : null,
      String(data.cob),
      String(data.gender),
      Number(data.childrenNo),
      String(data.visaNo),
      data.visaExpireDate
        ? format(new Date(data.visaExpireDate), "yyyy-MM-dd")
        : null,
      data?.jobTitle ? Number(data?.jobTitle) : null,
      String(data.department),
      Number(data.hourRate),
      Number(data.whpd),
      Number(data.whpw),
      data.contractStart
        ? format(new Date(data.contractStart), "yyyy-MM-dd")
        : null,
      data.contractEnd
        ? format(new Date(data.contractEnd), "yyyy-MM-dd")
        : null,
      Number(data.registrationNo),
      String(data.badgeId),
      data.shifts,
      String(data.workPermitNo),
      data.days,
      contractCopy,
      idCopyFront,
      personalPicture,
      passportCopy,
      visaCopy,
      covidCopy
    );

    if (response.status == 200) {
      setButtonStatus(0);
      cancelHandler();
      toggleSuccessModal(true);
    }

    return response.status;
  };

  const cancelHandler = () => {
    resetForm();
    setModalWidth(Math.max(window.innerWidth / 2, 514));
    setNewCancelModal(false);
    toggleAsideModalOpen(false);

    activateLoading(false);
    setInitChange(false);
  };

  return (
    <>
      <Modal
        size="tiny"
        open={toggled}
        onClose={() => {
          untoggle();
          cancelHandler();
        }}
        className={`assets-modal add-people-role ${
          selectedLanguage === "ar" ? "user-type-popup-rtl" : ""
        }`}
      >
        {isLoadingActive ? (
          <Dimmer active inverted>
            <Loader inverted content={t.common.loading} />
          </Dimmer>
        ) : (
          ""
        )}
        <div className="header-container">
          <span className="assets-header">{t.people.add_person}</span>
          <div>
            <span
              className="c-pointer text-right"
              onClick={() => {
                untoggle();
                cancelHandler();
              }}
            >
              <CloseIcon />
            </span>
          </div>
        </div>
        <Modal.Content>
          <List
            className="role-list"
            //  style={{ gap: "20px" }}
          >
            {dropdownOptions.roles.map((el) => {
              return (
                <Segment
                  key={el.key}
                  id={el.value}
                  className={`flexbox c-pointer people-popup ${
                    data.role === el.value ? "selected-role" : ""
                  }`}
                  // style={
                  //   data.role == el.value
                  //     ? {
                  //         background: "#E9F2FF",
                  //         borderColor: "#0C66E4",
                  //         cursor: "pointer",
                  //       }
                  //     : { cursor: "pointer" }
                  // }
                  onClick={() => {
                    setData({
                      ...data,
                      role: el.value,
                    });
                  }}
                >
                  <span>{el.icon}</span>

                  <div className="people-modal-text ml-3 mr-3">
                    <div className="bg-transparent">{el.text}</div>
                    <p>{el.description}</p>
                  </div>
                </Segment>
              );
            })}
          </List>
        </Modal.Content>

        <div
          className={`modal-button-container mt-4 ${
            selectedLanguage == "en" ? "mr-8" : "ml-10"
          }`}
        >
          <button
            onClick={() => {
              untoggle();
              cancelHandler();
            }}
            className="modal-form-action-button black"
            // style={{ color: "black" }}
          >
            {t.common.cancel}
          </button>

          <button
            disabled={!data.role}
            onClick={() => {
              activateLoading(true);
              setTimeout(() => {
                untoggle(false);
                toggleAsideModalOpen(true);
              }, 1000);
            }}
            className="modal-form-action-button light-blue"
          >
            {t.common.next}
          </button>
        </div>
      </Modal>
      <div
        clearing
        style={{ maxWidth: `${modalWidth}px` }}
        className={
          isAsideModalOpen
            ? "aside-modal preview active procedure"
            : "aside-modal preview procedure"
        }
      >
        <Resizable
          size={{ width: modalWidth, height: "auto" }}
          minWidth={"514px"}
          onResize={(e, direction, ref, d) => {
            setModalWidth(ref.offsetWidth);
          }}
        >
          <div className="modal-header detail-header">
            <div className="flexbox justify-between align-center">
              <div className="name">
                {t.people.personal_info_form.add_person}
              </div>
              <div className="flexbox align-center workorder-header-buttons">
                <span
                  className="position-relative procedure-header-button c-pointer"
                  onMouseOver={() => setCancleTooltip(true)}
                  onMouseLeave={() => setCancleTooltip(false)}
                  onClick={() => {
                    initChange ? setNewCancelModal(true) : cancelHandler();
                  }}
                >
                  <CloseIcon />
                  <ToolTip
                    color="#fff"
                    backgroundColor="#172B4D"
                    position={
                      selectedLanguage == "en"
                        ? "bottom center"
                        : "bottom center"
                    }
                    arrowAlign="center"
                    show={showCancleTooltip}
                    textBoxWidth="36px"
                    padding="5px 5px 5px 7px"
                    borderRadius="5px"
                    static
                  >
                    <span className="work-order-preview-tooltip">
                      {t.common.close}
                    </span>
                  </ToolTip>
                </span>
              </div>
            </div>
          </div>

          <div className="">
            <PeopleForm
                setData={setData}
                setPersonalImgId={setPersonalImgId}
                setFile={setFile}
                setImageSrc={setImageSrc}
                imageSrc={imageSrc}
                personalImgId={personalImgId}
                file={file}
                organisationId={organisationId}
                setDropdownOptions={setDropdownOptions}
                dropdownOptions={dropdownOptions}
                roleData={roleData}
                fetchJobTitleData={fetchJobTitleData}
                data={data}
                PersonValidationSchema={PersonValidationSchema}
                PersonTechnicianValidationSchema={PersonTechnicianValidationSchema}
                PersonRequesterValidationSchema={PersonRequesterValidationSchema}
                PersonLimitedValidationSchema={PersonLimitedValidationSchema}
                handleSubmit={createNewUserHandler}
                activePane={activePane}
                changeTab={changeTab}
                fetchCountries={fetchCountries}
                fetchLocations={fetchLocations}
                cancelHandler={cancelHandler}
                setNewCancelModal={setNewCancelModal}
                onFileChange={onFileChange}
                initChange={initChange}
                buttonStatus={buttonStatus}
                attachments={attachments}
                setAttachments={setAttachments}
                userProfileLoc={allIds}
                onCertificateFileChange={onCertificateFileChange}
            />
          </div>
        </Resizable>
      </div>
      <div
        className={
          isAsideModalOpen
            ? "aside-modal-background active"
            : "aside-modal-background"
        }
        onClick={() => (initChange ? setNewCancelModal(true) : cancelHandler())}
      ></div>

      <NewCancelModal
        yesCallback={() => {
          cancelHandler();
        }}
        title={t.parts.form.changes}
      >
        <p className="cancel-child"> {t.common.cancel_2statement}</p>
      </NewCancelModal>
    </>
  );
}

export default Add;
